import React, { useState } from "react";
import { Button, Form, Input, Picker, Toast, NavBar } from 'antd-mobile';
import { useNavigate, useLocation } from 'react-router-dom';
import { EyeInvisibleOutline, EyeOutline } from "antd-mobile-icons";
import type { PickerValue } from "antd-mobile/es/components/picker";
import { t } from "../../../../i18n";
import { isValidPassword } from "../../../../utils/passwordValidation";
import { getNavBarContainerStyle } from "../../../../utils/deviceUtils";
import { CHANGE_PASSWORD, ChangePasswordResponse, ChangePasswordVariables } from "../../../../graphql/mutations/changePassword";
import { parseExceptionMessageKey } from "../../../../utils/exceptionUtils";
import { NoAccountModal } from '../../../../components/NoAccountModal';
import { client } from "../../../../graphql/client";
import { useSendVerificationCode } from "../../../../hooks/useSendVerificationCode";
import { validatePhoneNumber } from "../../../../utils/phoneValidation";
import { validateVerificationCode } from "../../../../utils/verificationCodeValidation";

const countryCodes = [
    { label: '+1', value: '+1', flag: '🇺🇸' },
];

type Step = 'verifyPhone' | 'setPassword';

const ChangePasswordPage: React.FC = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [step, setStep] = useState<Step>('verifyPhone');
    const [verificationCode, setVerificationCode] = useState('');
    const { sendCode: sendChangePasswordCode, countdown } = useSendVerificationCode({
        type: 'change_password',
        getPhone: () => ({
            phone: form.getFieldValue('phoneOrEmail'),
            countryCode: form.getFieldValue('countryCode') || selectedCode
        }),
        onNotExist: () => setNoAccountModalVisible(true),
        onDisabled: () => Toast.show(t('errors.business.account.disabled')),
    });
    const [pickerVisible, setPickerVisible] = useState(false);
    const [selectedCode, setSelectedCode] = useState<string>('+1');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [verifiedPhone, setVerifiedPhone] = useState<string>('');
    const [verifiedCountryCode, setVerifiedCountryCode] = useState<string>('+1');
    const [noAccountModalVisible, setNoAccountModalVisible] = useState(false);

    // 获取验证码
    const handleGetVerificationCode = async () => {
        await sendChangePasswordCode();
    };

    // Step 1: 验证手机号
    const handleVerifyPhone = async (values: { countryCode: string; phoneOrEmail: string }) => {
        try {
            // 1. 校验手机号
            const phoneValidation = validatePhoneNumber(values.phoneOrEmail, values.countryCode);
            if (!phoneValidation.isValid) {
                Toast.show(phoneValidation.errorMessage!);
                return;
            }

            // 2. 校验验证码（包括格式和后端校验）
            const codeValidation = await validateVerificationCode(
                Number(values.countryCode.replace('+', '')),
                phoneValidation.phoneDigits!,
                verificationCode,
                {
                    type: 'change_password',
                    onNotExist: () => setNoAccountModalVisible(true)
                }
            );

            if (!codeValidation.isValid) {
                if (codeValidation.errorMessage) {
                    Toast.show(codeValidation.errorMessage);
                }
                return;
            }

            // 验证通过，进入下一步
            setVerifiedPhone(phoneValidation.phoneDigits!);
            setVerifiedCountryCode(values.countryCode);
            setStep('setPassword');
        } catch (error: any) {
            Toast.show(t('pages.login.changePasswordPage.verifyFailed'));
        }
    };

    // Step 2: 设置新密码
    const handleSetPassword = async () => {
        try {
            if (!newPassword || newPassword.trim() === '') {
                Toast.show(t('pages.login.changePasswordPage.newPasswordRequired'));
                return;
            }

            if (!confirmPassword || confirmPassword.trim() === '') {
                Toast.show(t('pages.login.changePasswordPage.confirmPasswordRequired'));
                return;
            }

            if (newPassword !== confirmPassword) {
                Toast.show(t('pages.login.changePasswordPage.passwordMismatch'));
                return;
            }

            // 验证密码格式（统一规则：8-16 位，含字母+数字+特殊字符，不分大小写）
            if (!isValidPassword(newPassword)) {
                Toast.show(t('pages.login.changePasswordPage.passwordFormatError'));
                return;
            }

            // 检查是否有手机号和国家代码
            if (!verifiedPhone || !verifiedCountryCode) {
                Toast.show(t('pages.login.changePasswordPage.verifyFailed'));
                return;
            }

            // 调用修改密码API
            const countryCodeNumber = Number(verifiedCountryCode.replace('+', ''));
            const { data, errors } = await client.mutate<ChangePasswordResponse, ChangePasswordVariables>({
                mutation: CHANGE_PASSWORD,
                variables: {
                    input: {
                        countryCode: countryCodeNumber,
                        phone: verifiedPhone,
                        newPassword: newPassword,
                        verificationCode: verificationCode
                    }
                }
            });

            if (errors) {
                const messageKey = parseExceptionMessageKey(errors[0]);
                if (messageKey) {
                    const translatedMessage = t(messageKey);
                    Toast.show(translatedMessage === messageKey ? t('errors.business.account.password.changeFailed') : translatedMessage);
                } else {
                    Toast.show(t('errors.business.account.password.changeFailed'));
                }
                return;
            }

            if (data?.changePassword.success) {
                Toast.show(t('pages.login.changePasswordPage.passwordChangedSuccess'));
                // 返回登录页，并传递手机号
                setTimeout(() => {
                    navigate('/login', {
                        state: {
                            phoneNumber: verifiedPhone,
                            countryCode: verifiedCountryCode,
                            loginType: 'password'
                        }
                    });
                }, 1500);
            } else {
                // message 字段可能是 messageKey，尝试翻译
                const message = data?.changePassword.message;
                if (message) {
                    const translatedMessage = t(message);
                    Toast.show(translatedMessage === message ? t('pages.login.changePasswordPage.changePasswordFailed') : translatedMessage);
                } else {
                    Toast.show(t('pages.login.changePasswordPage.changePasswordFailed'));
                }
            }
        } catch (error: any) {
            Toast.show(t('pages.login.changePasswordPage.changePasswordFailed'));
        }
    };

    const handleBack = () => {
        if (step === 'setPassword') {
            setStep('verifyPhone');
            setNewPassword('');
            setConfirmPassword('');
        } else {
            navigate('/login');
        }
    };

    return (
        <div className="min-h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar
                    onBack={handleBack}
                >
                    {t('pages.login.changePasswordPage.title')}
                </NavBar>
            </div>

            <div className="flex flex-col p-4 gap-6 mt-8">
                {step === 'verifyPhone' ? (
                    <>
                        <div className="text-lg font-semibold mb-2">
                            {t('pages.login.changePasswordPage.enterPhoneNumber')}
                            <span className="text-red-500">*</span>
                        </div>

                        <Form
                            form={form}
                            onFinish={handleVerifyPhone}
                            initialValues={{ countryCode: '+1' }}
                            layout="vertical"
                            style={{
                                '--border-top': 'none',
                                '--border-inner': 'none',
                                '--border-bottom': 'none',
                            }}
                        >
                            <div className="flex gap-2 mb-4">
                                <Form.Item name="countryCode" className="mb-0 w-[70px]">
                                    <div
                                        className="w-full h-full flex items-center justify-center gap-1 cursor-pointer"
                                        onClick={() => setPickerVisible(true)}
                                    >
                                        <span className="text-lg">
                                            {countryCodes.find(c => c.value === selectedCode)?.flag || '🇺🇸'}
                                        </span>
                                        <span>{selectedCode}</span>
                                    </div>
                                </Form.Item>

                                <Form.Item name="phoneOrEmail" className="mb-0 flex-1">
                                    <Input
                                        clearable
                                        placeholder={t('pages.login.changePasswordPage.phoneNumber')}
                                        type="tel"
                                        inputMode="tel"
                                        onChange={val => {
                                            const digitsOnly = val.replace(/\D/g, '');
                                            const maxLength = selectedCode === '+86' ? 11 : 10; // CN 11 digits, US 10 digits
                                            const limitedDigits = digitsOnly.slice(0, maxLength);
                                            form.setFieldValue('phoneOrEmail', limitedDigits);
                                        }}
                                    />
                                </Form.Item>
                            </div>

                            <Picker
                                columns={[countryCodes.map(code => ({
                                    label: `${code.flag} ${code.label}`,
                                    value: code.value
                                }))]}
                                visible={pickerVisible}
                                value={[selectedCode]}
                                onClose={() => setPickerVisible(false)}
                                onConfirm={(val: PickerValue[]) => {
                                    const newCode = String(val[0]);
                                    setSelectedCode(newCode);
                                    form.setFieldsValue({ countryCode: newCode });
                                    setPickerVisible(false);
                                }}
                            />

                            <div className="text-lg font-semibold mb-2">
                                {t('pages.login.changePasswordPage.verificationCode')}
                                <span className="text-red-500">*</span>
                            </div>

                            <Form.Item className="w-full mb-4">
                                <div className="adm-input">
                                    <Input
                                        className="adm-input-element"
                                        placeholder={t('common.verificationCode.placeholder')}
                                        type="tel"
                                        inputMode="numeric"
                                        value={verificationCode}
                                        onChange={val => {
                                            const digitsOnly = val.replace(/\D/g, '').slice(0, 6);
                                            setVerificationCode(digitsOnly);
                                        }}
                                    />
                                    <div className="px-2 whitespace-nowrap flex-shrink-0">
                                        <Button
                                            size="small"
                                            color="primary"
                                            fill="none"
                                            disabled={countdown > 0}
                                            onClick={handleGetVerificationCode}
                                        >
                                            {countdown > 0 ? `${countdown}s` : t('pages.login.changePasswordPage.getCode')}
                                        </Button>
                                    </div>
                                </div>
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    block
                                    color="primary"
                                    size="large"
                                    type="submit"
                                >
                                    {t('pages.login.changePasswordPage.ok')}
                                </Button>
                            </Form.Item>
                        </Form>
                    </>
                ) : (
                    <>
                        <div className="text-lg font-semibold mb-2">
                            {t('pages.login.changePasswordPage.enterNewPassword')}
                            <span className="text-red-500">*</span>
                        </div>

                        <Form.Item className="w-full mb-4">
                            <div className="adm-input">
                                <Input
                                    className="adm-input-element"
                                    placeholder={t('pages.login.changePasswordPage.enterPassword')}
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={val => {
                                        // 过滤掉空格
                                        const filteredValue = val.replace(/\s/g, '');
                                        setNewPassword(filteredValue);
                                    }}
                                />
                                <div
                                    className="cursor-pointer px-2"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                >
                                    {showNewPassword ? <EyeOutline /> : <EyeInvisibleOutline />}
                                </div>
                            </div>
                        </Form.Item>

                        <div className="text-lg font-semibold mb-2">
                            {t('pages.login.changePasswordPage.confirmNewPassword')}
                            <span className="text-red-500">*</span>
                        </div>

                        <Form.Item className="w-full mb-4">
                            <div className="adm-input">
                                <Input
                                    className="adm-input-element"
                                    placeholder={t('pages.login.changePasswordPage.confirmPassword')}
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={val => {
                                        // 过滤掉空格
                                        const filteredValue = val.replace(/\s/g, '');
                                        setConfirmPassword(filteredValue);
                                    }}
                                />
                                <div
                                    className="cursor-pointer px-2"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? <EyeOutline /> : <EyeInvisibleOutline />}
                                </div>
                            </div>
                        </Form.Item>

                        <div className="text-sm text-[#888] mb-4">
                            {t('pages.login.changePasswordPage.passwordRequirement')}
                        </div>

                        <Button
                            block
                            color="primary"
                            size="large"
                            onClick={handleSetPassword}
                        >
                            {t('pages.login.changePasswordPage.confirm')}
                        </Button>
                    </>
                )}

                {/* 手机号不存在弹窗提醒 */}
                <NoAccountModal
                    visible={noAccountModalVisible}
                    onClose={() => setNoAccountModalVisible(false)}
                />
            </div>
        </div>
    );
};

export default ChangePasswordPage;

